import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import '../users/userform.css';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../services/AxiosInstance";
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { isBranchAdmin, isDepartmentAdmin, getCurrentUserRole } from '../../utils/roleAccess';
import { FIELD_LIMITS } from '../../utils/fieldLimits';

const AddSubDepartment = () => {
 const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    branchId: "",
    departmentId: "",
    subDepartmentName: ""
  });
  const [isBranchDisabled, setIsBranchDisabled] = useState(false);
  const [isDepartmentDisabled, setIsDepartmentDisabled] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper function to safely navigate back, avoiding login page
  const navigateBackSafely = () => {
    navigate(-1);

    // Set a timeout to check if we ended up on login page
    setTimeout(() => {
      if (window.location.pathname.includes('/login')) {
        const search = searchParams.get('search') || '';
        navigate(`/task/projectlist${search ? `?search=${encodeURIComponent(search)}` : ''}`);
      }
    }, 100);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };
// Fetch branches for filter dropdown
  const fetchBranches = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId) {
        const response = await axiosInstance.get(`/settings/branches?corporate_id=${userData.corporateId}`);
        const branchesData = response.data.data || response.data || [];
        console.log('Branch data structure:', branchesData);
        setBranches(branchesData);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // Fetch departments for filter dropdown
  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId) {
        const response = await axiosInstance.get(`/settings/departments?corporate_id=${userData.corporateId}`);
        const departmentsData = response.data.data || response.data || [];
        console.log('Department data structure:', departmentsData);
        setDepartments(departmentsData);
        setFilteredDepartments(departmentsData); // Initialize filtered departments with all departments
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Function to filter departments based on selected branch
    const filterDepartmentsByBranch = useCallback((branchId) => {
      console.log('=== FILTERING DEPARTMENTS ===');
      console.log('Branch ID:', branchId);
      console.log('Available branches:', branches);
      console.log('Available departments:', departments);
      
      if (!branchId) {
        // If no branch selected, show all departments
        console.log('No branch selected, showing all departments');
        setFilteredDepartments(departments);
      } else {
        // Find the selected branch to get its name
        const selectedBranch = branches.find(branch => String(branch.id) === String(branchId));
        console.log('Selected branch:', selectedBranch);
        
        if (selectedBranch) {
          // Filter departments that belong to the selected branch
          const filtered = departments.filter(dept => {
            const matches = dept.branch_name === selectedBranch.name || 
                   String(dept.branch_id) === String(branchId) ||
                   String(dept.branch_id) === String(selectedBranch.id);
            console.log(`Department: ${dept.name}, branch_name: ${dept.branch_name}, branch_id: ${dept.branch_id}, matches: ${matches}`);
            return matches;
          });
          console.log('Filtered departments:', filtered);
          setFilteredDepartments(filtered);
        } else {
          console.log('Branch not found, showing all departments');
          setFilteredDepartments(departments);
        }
      }
      console.log('=== END FILTERING ===');
    }, [departments, branches]);
  

  const validate = () => {
    const newErrors = {};
    if (!formData.branchId || formData.branchId === '') newErrors.branchId = 'Branch is required';
    if (!formData.departmentId || formData.departmentId === '') newErrors.departmentId = 'Department is required';
    if (!formData.subDepartmentName.trim()) newErrors.subDepartmentName = 'Sub Department name is required';
    else if (formData.subDepartmentName.length > FIELD_LIMITS.SUB_DEPARTMENT_NAME) newErrors.subDepartmentName = `Sub Department name cannot exceed ${FIELD_LIMITS.SUB_DEPARTMENT_NAME} characters`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    setLoading(true);
    setIsSubmitting(true);

    const userData = JSON.parse(localStorage.getItem("user"));
    
    const payload = {
      name: formData.subDepartmentName,
      branch_id: formData.branchId,
      department_id: formData.departmentId,
      created_by: userData?.id,
    };
    
    try {
      const response = await axiosInstance.post(`/settings/addSubDepartment`, payload);

      if (!response) {
        throw new Error('Failed to submit the form');
      }

      const result = response;
      console.log('Form submitted successfully:', result);
      notifyTopRight('✅ Sub Department created successfully!');
      
      setTimeout(() => {
        setIsSubmitting(false);
        navigateBackSafely();
      }, 2000);

    } catch (error) {
      console.error('Submission error:', error);
      if (error.response?.status === 409) {
        notifyError('Sub Department already exists with this department');
      } else {
        notifyError(error.response?.data?.error || 'Failed to create sub department');
      }
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeAll = async () => {
      await fetchBranches();
      await fetchDepartments();
      await initializeRoleBasedFields();
    };
    
    initializeAll();
  }, []);

  const initializeRoleBasedFields = async () => {
    try {
      const role = getCurrentUserRole();
      
      if (isBranchAdmin(role) || isDepartmentAdmin(role)) {
        const userData = JSON.parse(localStorage.getItem('user'));
        const userId = userData?.id;
        
        if (userId) {
          try {
            const userDetailsRes = await axiosInstance.get(`/auth/details/${userId}`);
            const userDetails = userDetailsRes.data;
            
            const branchId = userDetails?.Branch?.id || userDetails?.BranchId;
            const departmentId = userDetails?.Department?.id || userDetails?.DepartmentId;
            
            // Branch admin: prefill and disable branch
            if (isBranchAdmin(role)) {
              if (branchId) {
                setFormData((prev) => ({ ...prev, branchId: branchId }));
                setIsBranchDisabled(true);
                // Fetch departments filtered by branch
                await fetchDepartmentsForBranch(branchId);
              }
            }
            
            // Department admin: prefill and disable branch and department
            if (isDepartmentAdmin(role)) {
              if (branchId) {
                setFormData((prev) => ({ ...prev, branchId: branchId }));
                setIsBranchDisabled(true);
                // Fetch departments filtered by branch first
                await fetchDepartmentsForBranch(branchId);
                
                if (departmentId) {
                  // Wait a bit for departments to be loaded, then set department
                  setTimeout(() => {
                    setFormData((prev) => ({ ...prev, departmentId: departmentId }));
                    setIsDepartmentDisabled(true);
                  }, 300);
                }
              }
            }
          } catch (err) {
            console.error('Error fetching current user details:', err);
          }
        }
      }
    } catch (err) {
      console.error('Error initializing role-based fields:', err);
    }
  };

  const fetchDepartmentsForBranch = async (branchId) => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId && branchId) {
        const response = await axiosInstance.get(`/settings/departments?corporate_id=${userData.corporateId}&branch_id=${branchId}`);
        const departmentsData = response.data.data || response.data || [];
        setDepartments(departmentsData);
        setFilteredDepartments(departmentsData);
      }
    } catch (error) {
      console.error('Error fetching departments for branch:', error);
    }
  };

  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  return (
    <div>
      <button
        className="back-button"
        onClick={() => {
          if (window.history.length > 2) {
            navigate(-1);
          } else {
            navigate('/settings/departmentlist');
          }
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <div className="row" style={{ marginBottom: 0 }}>
        <ToastContainer />
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
          <div className="card-body">
              <section>
                <div className="row">
                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="user-form-label mb-1"><strong>Branch*</strong></label>
                      <div className="user-form-select-container">
                        <Select
                          classNamePrefix="user-form-select"
                          placeholder="Select Branch"
                          isClearable
                          isDisabled={isBranchDisabled}
                          value={branches.find((branch) => branch.id === formData.branchId) 
                            ? { value: formData.branchId, label: branches.find(b => b.id === formData.branchId)?.name }
                            : null
                          }
                          onChange={(selectedOption) => {
                            const value = selectedOption ? selectedOption.value : '';
                            setFormData((prevData) => ({
                              ...prevData,
                              branchId: value,
                              departmentId: ''
                            }));
                            filterDepartmentsByBranch(value);
                          }}
                          options={branches.map((branch) => ({
                            value: branch.id,
                            label: branch.name
                          }))}
                        />
                      </div>
                      {errors.branchId && <div className="text-danger">{errors.branchId}</div>}
                    </div>
                  </div>
                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="user-form-label mb-1"><strong>Department*</strong></label>
                      <div className="user-form-select-container">
                        <Select
                          classNamePrefix="user-form-select"
                          placeholder="Select Department"
                          isClearable
                          isDisabled={isDepartmentDisabled}
                          value={filteredDepartments.find((dept) => dept.id === formData.departmentId) 
                            ? { value: formData.departmentId, label: filteredDepartments.find(d => d.id === formData.departmentId)?.name }
                            : null
                          }
                          onChange={(selectedOption) => {
                            setFormData((prevData) => ({
                              ...prevData,
                              departmentId: selectedOption ? selectedOption.value : ''
                            }));
                          }}
                          options={filteredDepartments.map((department) => ({
                            value: department.id,
                            label: department.name
                          }))}
                        />
                      </div>
                      {errors.departmentId && <div className="text-danger">{errors.departmentId}</div>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="text-label">Sub Department Name*</label>
                      <input
                        type="text"
                        name="subDepartmentName"
                        className="form-control"
                        placeholder="Enter sub department name"
                        maxLength={FIELD_LIMITS.SUB_DEPARTMENT_NAME}
                        value={formData.subDepartmentName}
                        onChange={handleChange}
                      />
                      {errors.subDepartmentName && <div className="text-danger">{errors.subDepartmentName}</div>}
                    </div>
                  </div>

                  <div className="text-end toolbar toolbar-bottom p-2">
                    <button 
                      type="button"
                      className="btn btn-cancel me-2" 
                      onClick={navigateBackSafely}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn user-form-btn sw-btn-next" 
                      onClick={handleSubmit} 
                      disabled={isSubmitting} 
                      style={{ maxWidth: '100px' }}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      ) : null}
                      Submit
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    </div>
  );
};

export default AddSubDepartment;